import axios from 'axios';

const BASE_URL_CIA_SERVICES = 'https://www.ciaservices.com';

export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

export const ciaServicesApiClient = axios.create({
  baseURL: BASE_URL_CIA_SERVICES,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.defaults.headers.common['Content-Type'] = 'application/json';

// Request interceptor to add token from localStorage
apiClient.interceptors.request.use(
  (config) => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.token) {
          // Add token to request
          if (config.data && typeof config.data === 'object') {
            config.data = { ...config.data, token: user.token };
          }
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Create event emitter for API errors
class ApiErrorEventEmitter {
  private listeners: Array<(error: ApiError) => void> = [];

  subscribe(listener: (error: ApiError) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  emit(error: ApiError) {
    this.listeners.forEach(listener => listener(error));
  }
}

export const apiErrorEmitter = new ApiErrorEventEmitter();

export interface ApiError {
  type: 'API_SUCCESS_STATUS_ERROR' | 'TOKEN_EXPIRED' | 'NETWORK_ERROR' | 'GENERIC_ERROR';
  message: string;
  originalError?: any;
}

apiClient.interceptors.response.use(
  (response) => {
    // Check if response has data and successStatus field
    if (response.data && typeof response.data.successStatus !== 'undefined') {
      // Check for success status
      if (response.data.successStatus !== 0) {
        const message = response.data.message || 'Something went wrong';

        // Check for token expiration or missing token
        const lowerMessage = message.toLowerCase();
        if (lowerMessage.includes('token is expired') || lowerMessage.includes('no security token supplied')) {
          const error: ApiError = {
            type: 'TOKEN_EXPIRED',
            message: message,
            originalError: response.data
          };
          apiErrorEmitter.emit(error);
          return Promise.reject(error);
        }

        // Generic API error with non-zero success status
        const error: ApiError = {
          type: 'API_SUCCESS_STATUS_ERROR',
          message: message,
          originalError: response.data
        };
        apiErrorEmitter.emit(error);
        return Promise.reject(error);
      }
    }

    return response;
  },
  async (error) => {
    // Network error or other axios errors
    let apiError: ApiError;

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      apiError = {
        type: 'NETWORK_ERROR',
        message: 'Something went wrong with the API call. Would you like to try again or try later?',
        originalError: error
      };
    } else if (error.request) {
      // The request was made but no response was received
      apiError = {
        type: 'NETWORK_ERROR',
        message: 'No response from server. Please check your connection and try again.',
        originalError: error
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      apiError = {
        type: 'GENERIC_ERROR',
        message: 'Something went wrong with the API call. Would you like to try again or try later?',
        originalError: error
      };
    }

    apiErrorEmitter.emit(apiError);
    return Promise.reject(apiError);
  },
);

// Add the same interceptor to ciaServicesApiClient
ciaServicesApiClient.interceptors.response.use(
  (response) => {
    // For ciaServicesApiClient, responses might be strings on error (as seen in existing code)
    // But we need to be careful not to treat all strings as errors
    // Only treat it as an error if it's a very specific error string pattern
    if (typeof response.data === 'string') {
      const lowerData = response.data.toLowerCase();

      // Check for token expiration or missing token in string responses
      if (lowerData.includes('token is expired') || lowerData.includes('no security token supplied')) {
        const error: ApiError = {
          type: 'TOKEN_EXPIRED',
          message: response.data,
          originalError: response.data
        };
        apiErrorEmitter.emit(error);
        return Promise.reject(error);
      }

      // Check for other error patterns
      if (lowerData.includes('fatal error') ||
          lowerData.includes('database error') ||
          lowerData.includes('connection failed') ||
          lowerData.includes('access denied')) {
        const error: ApiError = {
          type: 'API_SUCCESS_STATUS_ERROR',
          message: 'Something went wrong with the service request',
          originalError: response.data
        };
        apiErrorEmitter.emit(error);
        return Promise.reject(error);
      }
    }

    return response;
  },
  async (error) => {
    // Network error or other axios errors for ciaServicesApiClient
    let apiError: ApiError;

    if (error.response) {
      apiError = {
        type: 'NETWORK_ERROR',
        message: 'Something went wrong with the API call. Would you like to try again or try later?',
        originalError: error
      };
    } else if (error.request) {
      apiError = {
        type: 'NETWORK_ERROR',
        message: 'No response from server. Please check your connection and try again.',
        originalError: error
      };
    } else {
      apiError = {
        type: 'GENERIC_ERROR',
        message: 'Something went wrong with the API call. Would you like to try again or try later?',
        originalError: error
      };
    }

    apiErrorEmitter.emit(apiError);
    return Promise.reject(apiError);
  },
);
